import React, { useState } from 'react';
import { FaBiking, FaMapMarkerAlt, FaSearch, FaMountain, FaArrowUp, FaArrowDown, FaClock, FaExclamationTriangle, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import { analyzeMtbTrail, geocodeLocation } from '../../api/client';

const MountainBikeAuditor = () => {
  const [loading, setLoading] = useState(false);
  const [trailData, setTrailData] = useState(null);
  const [error, setError] = useState('');

  // Search inputs
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [trailName, setTrailName] = useState('');
  const [radiusKm, setRadiusKm] = useState(2);

  const handleSearch = async () => {
    if (!searchCity.trim()) {
      setError('Please enter a city or trail area');
      return;
    }

    setLoading(true);
    setError('');
    setTrailData(null);

    try {
      // First geocode the location
      const geoData = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (!geoData.results || geoData.results.length === 0) {
        setError('Location not found. Try a different search term.');
        setLoading(false);
        return;
      }

      const location = geoData.results[0];
      const name = trailName.trim() || searchCity;
      const locName = `${searchCity}${searchState ? ', ' + searchState : ''}`;

      // Get trail analysis
      const data = await analyzeMtbTrail(location.lat, location.lng, name, locName, radiusKm);
      setTrailData(data);
    } catch (err) {
      console.error('Error analyzing trail:', err);
      setError('Failed to analyze trail. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (rating) => {
    if (rating.includes('Green')) return 'bg-green-500';
    if (rating.includes('Blue')) return 'bg-blue-500';
    if (rating.includes('Double Black')) return 'bg-black';
    if (rating.includes('Black')) return 'bg-neutral-800';
    return 'bg-gray-500';
  };

  const getDifficultyIcon = (rating) => {
    if (rating.includes('Green')) return '●';
    if (rating.includes('Blue')) return '■';
    if (rating.includes('Double Black')) return '◆◆';
    if (rating.includes('Black')) return '◆';
    return '○';
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <svg className="absolute -right-20 -top-20 w-96 h-96" viewBox="0 0 100 100">
            <path d="M0,100 L50,20 L70,60 L100,0 L100,100 Z" fill="white" />
          </svg>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaBiking className="text-2xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Mountain Bike Trail Auditor</h2>
              <p className="text-white/70">Verify trail steepness using GIS data</p>
            </div>
          </div>

          <p className="text-white/80 mt-4">
            Enter a trail location to analyze elevation profiles, gradient steepness,
            and get difficulty ratings based on real terrain data.
          </p>
        </div>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaMapMarkerAlt className="text-orange-500" />
          Find Your Trail
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              City / Area *
            </label>
            <input
              type="text"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="e.g., Queenstown, Whistler"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              State / Region
            </label>
            <input
              type="text"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              placeholder="e.g., Otago, BC"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={searchCountry}
              onChange={(e) => setSearchCountry(e.target.value)}
              placeholder="e.g., New Zealand, Canada"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Trail Name (optional)
            </label>
            <input
              type="text"
              value={trailName}
              onChange={(e) => setTrailName(e.target.value)}
              placeholder="e.g., Skyline Trail"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Analysis Radius
            </label>
            <select
              value={radiusKm}
              onChange={(e) => setRadiusKm(parseFloat(e.target.value))}
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            >
              <option value={1}>1 km (Short loop)</option>
              <option value={2}>2 km (Medium)</option>
              <option value={5}>5 km (Long trail)</option>
              <option value={10}>10 km (Epic ride)</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <FaSearch />
          )}
          {loading ? 'Analyzing Trail...' : 'Analyze Trail'}
        </button>

        {error && (
          <p className="text-red-600 text-sm mt-3">{error}</p>
        )}
      </div>

      {/* Results */}
      {trailData && (
        <div className="space-y-6 animate-fade-in">
          {/* Main Rating Card */}
          <div className={`${getDifficultyColor(trailData.difficulty_rating)} rounded-2xl p-6 text-white`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{getDifficultyIcon(trailData.difficulty_rating)}</span>
                  <h3 className="text-2xl font-bold">{trailData.difficulty_rating}</h3>
                </div>
                <p className="text-white/90">{trailData.trail_name} - {trailData.location}</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold">{trailData.difficulty_score}</div>
                <p className="text-white/80 text-sm">Difficulty Score (1-10)</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <FaMountain className="text-2xl text-orange-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-neutral-800">{trailData.total_distance_km} km</div>
              <p className="text-neutral-500 text-sm">Distance</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <FaArrowUp className="text-2xl text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-neutral-800">{trailData.total_elevation_gain_m}m</div>
              <p className="text-neutral-500 text-sm">Elevation Gain</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <FaArrowDown className="text-2xl text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-neutral-800">{trailData.total_elevation_loss_m}m</div>
              <p className="text-neutral-500 text-sm">Elevation Loss</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <FaClock className="text-2xl text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-neutral-800">{trailData.estimated_time_minutes} min</div>
              <p className="text-neutral-500 text-sm">Est. Time</p>
            </div>
          </div>

          {/* Gradient Analysis */}
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-6">
            <h4 className="font-semibold text-neutral-800 mb-4">Gradient Analysis</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-xl font-bold text-neutral-700">{trailData.avg_gradient_percent}%</div>
                <p className="text-neutral-500 text-sm">Avg Gradient</p>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-orange-600">{trailData.max_gradient_percent}%</div>
                <p className="text-neutral-500 text-sm">Max Climb</p>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-red-600">{trailData.max_descent_gradient}%</div>
                <p className="text-neutral-500 text-sm">Max Descent</p>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-blue-600">{trailData.max_elevation_m}m</div>
                <p className="text-neutral-500 text-sm">Max Elevation</p>
              </div>
            </div>

            {/* Terrain Breakdown Bar */}
            <div className="mt-4">
              <p className="text-sm font-medium text-neutral-700 mb-2">Terrain Breakdown</p>
              <div className="h-4 bg-neutral-100 rounded-full overflow-hidden flex">
                <div className="bg-green-400 h-full" style={{ width: `${trailData.flat_percent}%` }} title={`Flat: ${trailData.flat_percent}%`} />
                <div className="bg-yellow-400 h-full" style={{ width: `${trailData.moderate_climb_percent}%` }} title={`Moderate climb: ${trailData.moderate_climb_percent}%`} />
                <div className="bg-orange-500 h-full" style={{ width: `${trailData.steep_climb_percent}%` }} title={`Steep climb: ${trailData.steep_climb_percent}%`} />
                <div className="bg-blue-400 h-full" style={{ width: `${trailData.moderate_descent_percent}%` }} title={`Moderate descent: ${trailData.moderate_descent_percent}%`} />
                <div className="bg-red-500 h-full" style={{ width: `${trailData.steep_descent_percent}%` }} title={`Steep descent: ${trailData.steep_descent_percent}%`} />
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-neutral-600">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded" /> Flat ({trailData.flat_percent}%)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-400 rounded" /> Mod. Climb ({trailData.moderate_climb_percent}%)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-500 rounded" /> Steep Climb ({trailData.steep_climb_percent}%)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-400 rounded" /> Mod. Descent ({trailData.moderate_descent_percent}%)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded" /> Steep Descent ({trailData.steep_descent_percent}%)</span>
              </div>
            </div>
          </div>

          {/* Recommendations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Skill & Bike */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-3">Requirements</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-neutral-500">Skill Level</p>
                  <p className="font-medium text-neutral-700">{trailData.skill_level_required}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Technical Rating</p>
                  <p className="font-medium text-neutral-700">{trailData.technical_rating}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Bike Recommendation</p>
                  <p className="font-medium text-neutral-700">{trailData.bike_type_recommendation}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Pace</p>
                  <p className="font-medium text-neutral-700">{trailData.pace_recommendation}</p>
                </div>
              </div>
            </div>

            {/* Gear Suggestions */}
            <div className="bg-orange-50 rounded-2xl p-5 border border-orange-100">
              <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <FaCheckCircle className="text-orange-500" />
                Gear Checklist
              </h4>
              <ul className="space-y-2">
                {trailData.gear_suggestions.map((gear, idx) => (
                  <li key={idx} className="text-orange-800 text-sm flex items-center gap-2">
                    <span className="text-orange-500">✓</span>
                    {gear}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Trail Features & Warnings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trailData.trail_features && trailData.trail_features.length > 0 && (
              <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <FaInfoCircle className="text-blue-500" />
                  Trail Features
                </h4>
                <ul className="space-y-2">
                  {trailData.trail_features.map((feature, idx) => (
                    <li key={idx} className="text-blue-800 text-sm flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {trailData.warnings && trailData.warnings.length > 0 && (
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <FaExclamationTriangle className="text-amber-500" />
                  Warnings
                </h4>
                <ul className="space-y-2">
                  {trailData.warnings.map((warning, idx) => (
                    <li key={idx} className="text-amber-800 text-sm flex items-start gap-2">
                      <span className="text-amber-500 mt-1">⚠</span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-orange-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-orange-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-orange-900 mb-1">About Trail Ratings</h4>
          <p className="text-sm text-orange-700">
            Difficulty ratings follow the ski resort system: Green (Easy), Blue (Intermediate),
            Black (Difficult), Double Black (Expert). Ratings are based on elevation gain,
            gradient steepness, and technical features derived from SRTM elevation data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MountainBikeAuditor;
